import { authService } from '../auth/authService';

const GRAPH_USERS_URL = 'https://graph.microsoft.com/v1.0/users';
const USER_SELECT = 'givenName,surname,displayName,mail,userPrincipalName';
const CACHE_KEY_PREFIX = 'wolfwalker.graphUser.';

/** @type {Map<string, string>} */
const memoryCache = new Map();

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function looksLikeEmail(value) {
  if (value == null || value === '') return false;
  return EMAIL_PATTERN.test(String(value).trim());
}

export function normalizeEmailKey(email) {
  return String(email || '').trim().toLowerCase();
}

export function formatGraphPersonName(user) {
  if (!user || typeof user !== 'object') return null;

  const given = String(user.givenName || '').trim();
  const family = String(user.surname || '').trim();
  if (given && family) return `${given} ${family}`;

  const displayName = String(user.displayName || '').trim();
  return displayName || null;
}

function readSessionCache(key) {
  try {
    return sessionStorage.getItem(`${CACHE_KEY_PREFIX}${key}`);
  } catch {
    return null;
  }
}

function writeSessionCache(key, value) {
  try {
    sessionStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, value);
  } catch {
    // ignore quota / private mode
  }
}

function cacheDisplayName(emailKey, displayName) {
  memoryCache.set(emailKey, displayName);
  writeSessionCache(emailKey, displayName);
}

function readCachedDisplayName(emailKey) {
  if (memoryCache.has(emailKey)) {
    return memoryCache.get(emailKey);
  }
  const stored = readSessionCache(emailKey);
  if (stored) {
    memoryCache.set(emailKey, stored);
    return stored;
  }
  return null;
}

function escapeODataString(value) {
  return String(value).replace(/'/g, "''");
}

async function fetchGraphUser(token, email) {
  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  };

  const directUrl = `${GRAPH_USERS_URL}/${encodeURIComponent(email)}?$select=${USER_SELECT}`;
  let response = await fetch(directUrl, { headers });

  if (response.ok) {
    return response.json();
  }

  if (response.status !== 404) {
    console.warn('Graph user lookup failed:', email, response.status);
    return null;
  }

  const filter = encodeURIComponent(
    `mail eq '${escapeODataString(email)}' or userPrincipalName eq '${escapeODataString(email)}'`
  );
  const searchUrl = `${GRAPH_USERS_URL}?$select=${USER_SELECT}&$filter=${filter}&$top=1`;
  response = await fetch(searchUrl, { headers });

  if (!response.ok) {
    console.warn('Graph user filter lookup failed:', email, response.status);
    return null;
  }

  const payload = await response.json();
  return Array.isArray(payload?.value) && payload.value.length ? payload.value[0] : null;
}

/**
 * Resolve a single email/UPN to a display name. Falls back to the input email.
 */
export async function resolveUserDisplayName(email) {
  const raw = String(email || '').trim();
  if (!raw || !looksLikeEmail(raw)) return raw;

  const key = normalizeEmailKey(raw);
  const cached = readCachedDisplayName(key);
  if (cached) return cached;

  try {
    const token = await authService.getGraphDirectoryToken();
    const user = await fetchGraphUser(token, raw);
    const name = formatGraphPersonName(user) || raw;
    cacheDisplayName(key, name);
    return name;
  } catch (error) {
    console.warn('Unable to resolve user display name:', raw, error);
    return raw;
  }
}

/**
 * Batch-resolve emails to display names. Returns Map(normalizedEmail -> name).
 */
export async function resolveUserDisplayNames(emails) {
  const unique = [
    ...new Set(
      (emails || [])
        .map((entry) => String(entry || '').trim())
        .filter(looksLikeEmail)
    ),
  ];

  const result = new Map();

  await Promise.all(
    unique.map(async (email) => {
      const key = normalizeEmailKey(email);
      const name = await resolveUserDisplayName(email);
      result.set(key, name);
    })
  );

  return result;
}

export default {
  looksLikeEmail,
  normalizeEmailKey,
  formatGraphPersonName,
  resolveUserDisplayName,
  resolveUserDisplayNames,
};
